import {hashKey, type QueryKey, useQueryClient} from '@github-ui/react-query'
import {useAlive} from '@github-ui/use-alive'
import {useCallback} from 'react'
import type {PullRequestAliveData} from './use-refetch-on-alive-update'
import {useThrottledCallback} from '@tanstack/react-pacer'

export interface UseInvalidateOnAliveUpdateConfig {
  /**
   * Control how keys are matched in the store when invalidating. Defaults to exact matching.
   * @see https://tanstack.com/query/latest/docs/framework/react/guides/filters#query-filters
   * @default false
   */
  partialKeyMatching?: boolean
  /** Option filter for events; return `false` to ignore an event. */
  filter?: (data: PullRequestAliveData) => boolean
  /** Throttle timeout to limit frequency of invalidations. Defaults to 2s. */
  throttleTimeout?: number
}

const pendingInvalidations = new Set<string>()

/**
 * Invalidate a query whenever a matching alive update arrives on a channel. Deduped across multiple updates for the
 * same query key.
 */
export function useInvalidateOnAliveUpdate(
  channel: string,
  queryKey: QueryKey,
  config?: UseInvalidateOnAliveUpdateConfig,
) {
  const queryClient = useQueryClient()

  const exact = !(config?.partialKeyMatching ?? false)
  const filter = config?.filter

  const throttledInvalidate = useThrottledCallback(
    (...args: Parameters<typeof queryClient.invalidateQueries>) => queryClient.invalidateQueries(...args),
    {wait: config?.throttleTimeout ?? 2000},
  )

  const handleNotification = useCallback(
    (data: PullRequestAliveData) => {
      const shouldHandle = filter?.(data) ?? true
      if (!shouldHandle) return

      const invalidateParams = {queryKey, exact}
      const hashedInvalidateParams = JSON.stringify(invalidateParams, (k, v) => (k === 'queryKey' ? hashKey(v) : v))

      // Deduplicate multiple simultaneous calls (typically from multiple mounts of the same hook). Note: it's tempting
      // to throttle globally instead: this would almost work but would still result in extra calls because two
      // simultaneous throttle calls still results in two calls (one `start` and one `end` call).
      if (pendingInvalidations.has(hashedInvalidateParams)) return
      pendingInvalidations.add(hashedInvalidateParams)

      setTimeout(
        () => {
          pendingInvalidations.delete(hashedInvalidateParams)
          throttledInvalidate(invalidateParams)
        },
        // Add a small delay to ensure simultaneous calls are collected
        (data.wait ?? 0) + 25,
      )
    },
    [exact, filter, queryKey, throttledInvalidate],
  )

  useAlive(channel, handleNotification)
}
